// What a price book service uses (pages/ux-full components/setup.js ItemUses), from pricebook.getItem's
// materials and details: each part and piece of equipment one sale puts in, how many, and our cost on each
// only when the answer carries it (pricebook.cost.view); then its sold hours, add-on price and warranty.
// A list, not a table, so a phone shows the cost beside each part with no sideways scroll.
// Read only: ServiceTitan's while the company mirrors it.
import type { ItemTerms, ItemUse, ItemUsesProps } from '../pricebook-uses-schemas.ts';
import { money } from '../format.ts';
import { Facts } from '../primitives.tsx';

/** "2.000" → "2", "1.500" → "1.5" */
export const qtyWords = (q: string) => Number(q).toLocaleString('en-CA', { maximumFractionDigits: 3 });

/** Our cost of what one sale uses, in cents; null when any part's cost is unknown or not shown. */
export function usesCost(uses: readonly ItemUse[]): number | null {
  let total = 0;
  for (const u of uses) {
    if (u.costCents == null) return null;
    total += u.costCents * Number(u.quantity);
  }
  return Math.round(total);
}

/** The terms as facts rows: only the ones it has. */
export function termRows(d: ItemTerms | null): [string, string][] {
  if (!d) return [];
  const rows: [string, string][] = [];
  if (d.soldHours) rows.push(['Sold as', `${qtyWords(d.soldHours)} hours`]);
  if (d.addOnPriceCents != null)
    rows.push([
      'As an add-on',
      `${money(d.addOnPriceCents)}${d.addOnMemberPriceCents != null ? `, members ${money(d.addOnMemberPriceCents)}` : ''}`,
    ]);
  if (d.warrantyText) rows.push(['Warranty', d.warrantyText]);
  return rows;
}

export function ItemUses({
  data,
  empty = 'No parts or equipment listed for it.',
}: {
  data: ItemUsesProps;
  /** What to say when nothing is listed; null says nothing (an item that isn't a service). */
  empty?: string | null;
}) {
  // the server leaves our cost out for whoever may not see it: so do the costs and their total
  const cost = data.uses.some((u) => 'costCents' in u);
  const total = cost ? usesCost(data.uses) : null;
  const terms = termRows(data.details);
  return (
    <div className="flw-uses">
      {data.uses.length ? (
        <>
          <ul className="flw-uses-list" aria-label="Parts and equipment">
            {data.uses.map((u) => (
              <li key={u.itemId} className={u.active ? undefined : 'flw-off'}>
                <span className="flw-uses-q">{qtyWords(u.quantity)} ×</span>
                <span className="flw-uses-n">
                  <span className="flw-open">{u.name}</span>
                  <span className="flw-sub">
                    {u.code}, {u.type === 'equipment' ? 'equipment' : 'part'}
                    {u.active ? '' : ', no longer offered'}
                  </span>
                </span>
                {cost && (
                  <span className="flw-uses-c">
                    {u.costCents == null
                      ? '—'
                      : `${money(u.costCents)} ${u.unit === 'each' ? 'each' : `a ${u.unit}`}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {total != null && (
            <p className="flw-uses-sum">
              <span className="flw-sub">Parts at our cost</span>
              <b>{money(total)}</b>
            </p>
          )}
        </>
      ) : (
        empty !== null && (
          <p className="flw-empty" role="status">
            {empty}
          </p>
        )
      )}
      {terms.length > 0 && <Facts rows={terms} />}
    </div>
  );
}
